import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CodeRounded from '@mui/icons-material/CodeRounded';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {
    colors,
    fontSize,
    fontWeight,
    borderRadius,
    spacing,
    letterSpacing,
    borderWidth,
    decorSize,
    shadows,
    gradients
} from '@/styles/tokens';
import type {techStackContent} from '@/util/contents/drawpost-data';

interface TechStackStripProps {
    content: typeof techStackContent;
}

const wrapperSx = {
    position: 'relative',
    zIndex: 2,
    mt: {xs: `-${spacing[96]}`, md: `-${spacing[60]}`},
    mb: {xs: `-${spacing[96]}`, md: `-${spacing[60]}`}
} as const;

const stripCardSx = {
    position: 'relative',
    overflow: 'hidden',
    bgcolor: colors.background.default,
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius['3xl'],
    boxShadow: shadows.elevated,
    width: 'fit-content',
    maxWidth: '100%',
    mx: 'auto',
    p: spacing[24],
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: spacing[16],
    columnGap: spacing[28]
} as const;

const glowOrbSx = {
    position: 'absolute',
    top: `-${spacing[64]}`,
    right: `-${spacing[64]}`,
    width: decorSize.orbSm,
    height: decorSize.orbSm,
    borderRadius: borderRadius.circle,
    background: gradients.techGlow,
    filter: `blur(${decorSize.blur})`,
    pointerEvents: 'none'
} as const;

const labelGroupSx = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: spacing[12],
    flexShrink: 0
} as const;

const iconBoxSx = {
    width: spacing[40],
    height: spacing[40],
    bgcolor: colors.accent.indigo,
    borderRadius: borderRadius.xl,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
} as const;

const labelTextSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.bold,
    color: colors.text.muted,
    letterSpacing: letterSpacing.mid,
    whiteSpace: 'nowrap'
} as const;

const chipListSx = {
    position: 'relative',
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[8]
} as const;

const chipSx = {
    display: 'inline-flex',
    alignItems: 'center',
    px: spacing[16],
    py: spacing[8],
    borderRadius: borderRadius.md,
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    bgcolor: colors.background.slate50,
    color: colors.text.secondary,
    fontSize: fontSize[16],
    fontWeight: fontWeight.medium,
    letterSpacing: letterSpacing.normal,
    whiteSpace: 'nowrap'
} as const;

const codeIconSx = {color: colors.text.inverse, fontSize: fontSize[20]} as const;

const TechStackStrip = memo(function TechStackStrip({content}: TechStackStripProps) {
    return (
        <MUIContainer>
            <Box sx={wrapperSx}>
                <Box sx={stripCardSx}>
                    <Box sx={glowOrbSx} aria-hidden="true" />

                    <Box sx={labelGroupSx}>
                        <Box sx={iconBoxSx}>
                            <CodeRounded sx={codeIconSx} />
                        </Box>
                        <Typography sx={labelTextSx}>Tech Stack</Typography>
                    </Box>

                    <Box sx={chipListSx}>
                        {content.pills.map((tech) => (
                            <Box key={tech} sx={chipSx}>
                                {tech}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </MUIContainer>
    );
});

export default TechStackStrip;
